import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { listLibrary, listSyncs } from './api';

const KINDS = ['', 'course', 'bundle', 'pathway', 'exam'];

export default function ContentLibrary() {
  const [kind, setKind] = useState('');
  const [program, setProgram] = useState('');
  const [level, setLevel] = useState('');
  const [delivery, setDelivery] = useState('');
  const [q, setQ] = useState('');
  const params = [
    kind && `kind=${kind}`,
    program && `program=${program}`,
    level && `level=${level}`,
    delivery && `delivery=${delivery}`,
    q && `q=${encodeURIComponent(q)}`,
  ].filter(Boolean).join('&');

  const { data, isLoading } = useQuery({
    queryKey: ['training-library', kind, program, level, delivery, q],
    queryFn: () => listLibrary(params ? `?${params}` : ''),
  });
  // Read-only sync status; the "Sync now" action lives in the admin Catalog tab.
  const { data: syncs } = useQuery({ queryKey: ['training-syncs'], queryFn: listSyncs, retry: false });

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-semibold">ZU Content Library</h2>
        {syncs && (
          <span className="text-xs text-gray-500">
            {syncs.item_count} items{syncs.runs?.[0] && ` · last ${syncs.runs[0].status}`}
          </span>
        )}
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        {KINDS.map((k) => (
          <button
            key={k || 'all'}
            onClick={() => setKind(k)}
            className={`text-xs px-3 py-1.5 rounded-full border ${
              kind === k ? 'bg-blue-50 border-blue-400 text-blue-600' : 'border-gray-300 dark:border-gray-600 text-gray-500'
            }`}
          >
            {k ? k[0].toUpperCase() + k.slice(1) + 's' : 'All kinds'}
          </button>
        ))}
        <select
          value={program}
          onChange={(e) => setProgram(e.target.value)}
          className="text-xs px-3 py-1.5 rounded-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 dark:[&>option]:bg-gray-800"
        >
          <option value="">All programs</option>
          {['LIHTC', 'HUD', 'RD', 'HOME', 'fair_housing'].map((p) => (
            <option key={p} value={p}>{p}</option>
          ))}
        </select>
        <select
          value={level}
          onChange={(e) => setLevel(e.target.value)}
          className="text-xs px-3 py-1.5 rounded-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 dark:[&>option]:bg-gray-800"
        >
          <option value="">All levels</option>
          {['introductory', 'intermediate', 'advanced'].map((l) => (
            <option key={l} value={l}>{l}</option>
          ))}
        </select>
        <select
          value={delivery}
          onChange={(e) => setDelivery(e.target.value)}
          className="text-xs px-3 py-1.5 rounded-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 dark:[&>option]:bg-gray-800"
        >
          <option value="">All delivery</option>
          {['elearning', 'live'].map((d) => (
            <option key={d} value={d}>{d}</option>
          ))}
        </select>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search…"
          className="text-xs px-3 py-1.5 rounded-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 dark:[&>option]:bg-gray-800"
        />
      </div>

      {isLoading ? (
        <div className="text-sm text-gray-500">Loading…</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {data?.items?.map((i) => (
            <div key={i.id} className="border border-gray-200 dark:border-gray-700 rounded-xl p-4 hover:shadow-md transition">
              <div className="text-[10px] uppercase tracking-wide text-gray-400 font-bold">
                {i.kind} · {i.delivery_type}
              </div>
              <h3 className="text-sm font-semibold my-1.5 leading-snug">{i.title}</h3>
              <div className="flex gap-1.5 flex-wrap mb-2">
                {i.program && <span className="text-[9.5px] px-2 py-0.5 rounded bg-blue-50 text-blue-700">{i.program}</span>}
                {(i.tags || []).slice(0, 2).map((t) => (
                  <span key={t} className="text-[9.5px] px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-700">{t}</span>
                ))}
              </div>
              <div className="text-xs text-gray-500 flex justify-between">
                <span>{i.level}</span>
                <span>{i.duration_minutes ? `${i.duration_minutes} min` : ''}</span>
              </div>
              <div className="text-xs text-gray-400 mt-2">{i.objective_count} objectives</div>
            </div>
          ))}
          {data?.items?.length === 0 && (
            <div className="text-sm text-gray-500 col-span-full">No catalog items yet - run a sync.</div>
          )}
        </div>
      )}
    </div>
  );
}
